<template>
  <Transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2"
      @click.self="close"
    >
      <div class="bg-white rounded-2xl shadow-2xl w-full h-full max-w-[98vw] max-h-[98vh] overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="bg-gradient-to-r from-cyan-600 to-blue-600 text-white p-4 flex-shrink-0">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <div>
                <h2 class="text-xl font-bold">SEC Cloud Analyzer</h2>
                <p class="text-cyan-100 text-sm">Analýza logů z Climatix Cloud</p>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <!-- Ruler toggle -->
              <button
                v-if="chartData"
                @click="toggleRuler"
                class="flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors"
                :class="showRuler ? 'bg-white text-cyan-600' : 'bg-white/20 text-white hover:bg-white/30'"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                <span class="text-sm">Pravítko</span>
              </button>
              <!-- Reset button -->
              <button
                v-if="chartData"
                @click="reset"
                class="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                title="Nová analýza"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <!-- Close button -->
              <button
                @click="close"
                class="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-hidden flex flex-col">
          <!-- Upload Area -->
          <div
            v-if="!isProcessing && !chartData"
            class="flex-1 p-6 flex flex-col"
          >
            <div
              @drop.prevent="handleDrop"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              :class="[
                'flex-1 border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer flex flex-col items-center justify-center',
                isDragging
                  ? 'border-cyan-500 bg-cyan-50'
                  : 'border-gray-300 hover:border-cyan-400 hover:bg-gray-50',
              ]"
              @click="triggerFileInput"
            >
              <input
                ref="fileInput"
                type="file"
                accept=".csv"
                @change="handleFileSelect"
                class="hidden"
              />

              <div class="space-y-4">
                <svg class="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>

                <div>
                  <p class="text-lg font-semibold text-gray-700">
                    Přetáhněte CSV soubor sem
                  </p>
                  <p class="text-sm text-gray-500 mt-1">
                    nebo klikněte pro výběr souboru
                  </p>
                </div>

                <div class="text-xs text-gray-400">
                  <p>Podporované formáty: CSV export z Climatix Cloud (SEC)</p>
                </div>
              </div>
            </div>

            <!-- Selected File -->
            <div v-if="selectedFile" class="mt-4">
              <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div class="flex items-center gap-3">
                  <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p class="text-sm font-medium text-gray-700">{{ selectedFile.name }}</p>
                    <p class="text-xs text-gray-500">{{ formatFileSize(selectedFile.size) }}</p>
                  </div>
                </div>
                <button
                  @click.stop="selectedFile = null"
                  class="text-red-600 hover:bg-red-50 rounded-full p-1 transition-colors"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <button
                @click="startAnalysis"
                class="w-full mt-4 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Spustit analýzu
              </button>
            </div>
          </div>

          <!-- Processing State -->
          <div v-if="isProcessing" class="flex-1 flex flex-col items-center justify-center p-6">
            <div class="inline-flex items-center justify-center space-x-2 mb-4">
              <div class="w-4 h-4 bg-cyan-600 rounded-full animate-bounce"></div>
              <div class="w-4 h-4 bg-blue-600 rounded-full animate-bounce" style="animation-delay: 0.1s"></div>
              <div class="w-4 h-4 bg-cyan-600 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
            </div>

            <h3 class="text-xl font-bold text-gray-800 mb-2">Analyzuji data...</h3>
            <p class="text-gray-600 mb-6">{{ processingStatus }}</p>

            <div class="w-full max-w-md bg-gray-100 rounded-lg p-4">
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="bg-gradient-to-r from-cyan-600 to-blue-600 h-2 rounded-full transition-all duration-500"
                  :style="{ width: processingProgress + '%' }"
                ></div>
              </div>
              <p class="text-sm text-gray-600 mt-2 text-center">{{ processingProgress }}%</p>
            </div>
          </div>

          <!-- Chart View -->
          <div v-if="chartData && !isProcessing" class="flex-1 flex flex-col overflow-hidden relative">
            <!-- Chart toolbar -->
            <div class="flex items-center justify-between px-4 py-2 bg-gray-50 border-b flex-shrink-0">
              <div class="flex items-center gap-4">
                <span class="text-sm text-gray-600">
                  <strong>{{ chartData.metadata.siteName }}</strong>
                  &bull; {{ chartData.timestamps.length }} záznamů
                  &bull; {{ formatDateRange(chartData.timestamps[0], chartData.timestamps[chartData.timestamps.length - 1]) }}
                </span>
              </div>
              <div class="flex items-center gap-2">
                <button
                  @click="downloadCSV"
                  class="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-200"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Export
                </button>
              </div>
            </div>

            <!-- Plotly chart container -->
            <div class="flex-1 relative min-h-[500px]" ref="chartContainer">
              <div ref="plotlyDiv" class="absolute inset-0"></div>

              <!-- Floating Ruler -->
              <div
                v-if="showRuler && rulerData"
                class="absolute top-0 bottom-0 pointer-events-none z-10"
                :style="{ left: rulerX + 'px' }"
              >
                <!-- Vertical line -->
                <div class="w-px h-full bg-gray-800 opacity-60"></div>

                <!-- Value panel -->
                <div
                  class="absolute top-4 pointer-events-auto bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-200 p-3 min-w-[200px] max-w-[280px] max-h-[70vh] overflow-y-auto"
                  :class="rulerPanelPosition === 'left' ? 'right-4' : 'left-4'"
                >
                  <div class="flex items-center justify-between mb-2 pb-2 border-b">
                    <span class="text-xs font-semibold text-gray-800">
                      {{ formatRulerTime(rulerData.timestamp) }}
                    </span>
                    <button
                      @click="showRuler = false"
                      class="text-gray-400 hover:text-gray-600 p-0.5"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div class="space-y-1">
                    <div
                      v-for="value in rulerData.values"
                      :key="value.id"
                      class="flex items-center justify-between text-xs gap-2"
                    >
                      <div class="flex items-center gap-1.5 min-w-0 flex-1">
                        <span
                          class="w-2 h-2 rounded-full flex-shrink-0"
                          :style="{ backgroundColor: value.color }"
                        ></span>
                        <span class="text-gray-600 truncate">{{ value.label }}</span>
                      </div>
                      <span class="font-medium text-gray-900 flex-shrink-0">
                        {{ formatValue(value.value, value.decimals) }}
                        <span class="text-gray-500 font-normal">{{ value.unit }}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Error State -->
          <div v-if="error" class="p-6">
            <div class="bg-red-50 border border-red-200 rounded-lg p-4">
              <div class="flex items-start gap-3">
                <svg class="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 class="font-semibold text-red-800">Chyba při analýze</h4>
                  <p class="text-sm text-red-600 mt-1">{{ error }}</p>
                  <button
                    @click="reset"
                    class="mt-2 text-sm text-red-700 hover:text-red-900 underline"
                  >
                    Zkusit znovu
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick, computed } from 'vue';
import Plotly from 'plotly.js-dist-min';

// Import datapoints mapping
import datapointsMapping from '../../docs/analyzer_datapoints_mapping.json';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'analysis-complete']);

// State
const selectedFile = ref(null);
const isDragging = ref(false);
const isProcessing = ref(false);
const processingProgress = ref(0);
const processingStatus = ref('Načítám soubor...');
const chartData = ref(null);
const error = ref(null);
const fileInput = ref(null);

// Chart refs
const chartContainer = ref(null);
const plotlyDiv = ref(null);

// Ruler state
const showRuler = ref(false);
const rulerX = ref(100);
const rulerData = ref(null);

// Create datapoint lookup map
const datapointLookup = computed(() => {
  const map = new Map();
  if (datapointsMapping?.dataPoints) {
    datapointsMapping.dataPoints.forEach(dp => {
      map.set(dp.datapointId, dp);
    });
  }
  return map;
});

// Ruler panel position (left or right of cursor)
const rulerPanelPosition = computed(() => {
  if (!chartContainer.value) return 'right';
  const containerWidth = chartContainer.value.offsetWidth;
  return rulerX.value > containerWidth / 2 ? 'left' : 'right';
});

// Methods
const close = () => {
  emit('close');
};

const reset = () => {
  selectedFile.value = null;
  isProcessing.value = false;
  processingProgress.value = 0;
  processingStatus.value = 'Načítám soubor...';
  chartData.value = null;
  error.value = null;
  showRuler.value = false;
  rulerData.value = null;

  // Clean up Plotly
  if (plotlyDiv.value) {
    Plotly.purge(plotlyDiv.value);
  }
};

const triggerFileInput = () => {
  fileInput.value?.click();
};

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (file && file.name.endsWith('.csv')) {
    selectedFile.value = file;
    error.value = null;
  }
};

const handleDrop = (event) => {
  isDragging.value = false;
  const file = event.dataTransfer.files[0];
  if (file && file.name.endsWith('.csv')) {
    selectedFile.value = file;
    error.value = null;
  }
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const formatDateRange = (start, end) => {
  const opts = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };
  return `${start.toLocaleString('cs-CZ', opts)} - ${end.toLocaleString('cs-CZ', opts)}`;
};

const formatRulerTime = (timestamp) => {
  if (!timestamp) return '';
  return timestamp.toLocaleString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const formatValue = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) return '---';
  return value.toFixed(decimals);
};

const toggleRuler = () => {
  showRuler.value = !showRuler.value;
};

// Parse SEC Cloud CSV
async function parseSecCloudCSV(content) {
  const lines = content.split('\n').map(line => line.trim()).filter(line => line);

  // Find metadata header (starts with "Site;Object name")
  let metadataStartIndex = -1;
  let dataStartIndex = -1;

  for (let i = 0; i < lines.length; i++) {
    if (lines[i].startsWith('Site;') || lines[i].startsWith('"Site"')) {
      metadataStartIndex = i;
    }
    if (lines[i].startsWith('Your time;') || lines[i].startsWith('"Your time"')) {
      dataStartIndex = i;
      break;
    }
  }

  if (metadataStartIndex === -1 || dataStartIndex === -1) {
    throw new Error('Neplatný formát CSV - nenalezen metadata header nebo data section');
  }

  // Parse metadata section
  const metadataLines = lines.slice(metadataStartIndex + 1, dataStartIndex);
  const columnMap = new Map();
  let siteName = '';

  metadataLines.forEach(line => {
    const parts = parseCSVLine(line);
    if (parts.length >= 5) {
      const [site, objectName, objectDescription, memberName, datapointId, unitSystem, unit] = parts;
      siteName = site.replace(/"/g, '');

      const cleanDatapointId = datapointId.replace(/"/g, '');
      const cleanUnit = (unit || '').replace(/"/g, '').trim();
      const cleanObjectName = objectName.replace(/"/g, '');
      const cleanDescription = objectDescription.replace(/"/g, '');

      // Try to find in mapping
      const mappingEntry = datapointLookup.value.get(cleanDatapointId);

      columnMap.set(cleanDatapointId, {
        datapointId: cleanDatapointId,
        objectName: cleanObjectName,
        objectDescription: cleanDescription,
        unit: cleanUnit,
        // From mapping or defaults
        label: mappingEntry?.display?.label || cleanObjectName,
        category: mappingEntry?.display?.category || 'Other',
        color: mappingEntry?.display?.color || generateColor(cleanDatapointId),
        range: mappingEntry?.range || { min: 0, max: 100, decimals: 1 },
        axisGroup: mappingEntry?.graph?.axisGroup || 'dimensionless'
      });
    }
  });

  // Parse data header to get column order
  const dataHeader = parseCSVLine(lines[dataStartIndex]);
  const datapointColumns = dataHeader.slice(4).map(col => col.replace(/"/g, ''));

  // Parse data rows
  const timestamps = [];
  const values = new Map();

  // Initialize value arrays
  datapointColumns.forEach(dpId => {
    values.set(dpId, []);
  });

  const dataLines = lines.slice(dataStartIndex + 1);
  const totalLines = dataLines.length;

  for (let i = 0; i < totalLines; i++) {
    const line = dataLines[i];
    if (!line) continue;

    const parts = parseCSVLine(line);
    if (parts.length < 5) continue;

    // Parse timestamp (format: "2026-01-02 09:40:00,000")
    const timeStr = parts[0].replace(/"/g, '');
    const timestamp = parseTimestamp(timeStr);
    if (!timestamp) continue;

    timestamps.push(timestamp);

    // Parse values
    for (let j = 0; j < datapointColumns.length; j++) {
      const dpId = datapointColumns[j];
      const valueStr = parts[4 + j] || '';
      const value = parseNumericValue(valueStr);
      values.get(dpId)?.push(value);
    }

    // Update progress
    if (i % 500 === 0) {
      processingProgress.value = Math.round(30 + (i / totalLines) * 50);
      await nextTick();
    }
  }

  return {
    metadata: {
      siteName,
      columnMap
    },
    timestamps,
    values,
    datapointColumns
  };
}

// Parse CSV line handling quotes
function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ';' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);

  return result;
}

// Parse timestamp
function parseTimestamp(str) {
  // Format: "2026-01-02 09:40:00,000" or "2026-01-02 09:40:00"
  try {
    const cleanStr = str.replace(',', '.');
    const date = new Date(cleanStr);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

// Parse numeric value
function parseNumericValue(str) {
  if (!str || str === '---' || str === '') return null;
  // Handle European decimal separator
  const cleanStr = str.replace(/"/g, '').replace(',', '.').trim();
  const value = parseFloat(cleanStr);
  return isNaN(value) ? null : value;
}

// Generate color based on string hash
function generateColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    '#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6',
    '#1abc9c', '#e67e22', '#34495e', '#16a085', '#c0392b',
    '#2980b9', '#27ae60', '#f1c40f', '#8e44ad', '#d35400'
  ];
  return colors[Math.abs(hash) % colors.length];
}

// Calculate axis groups based on actual data ranges
function calculateAxisGroups(data) {
  const columnRanges = [];

  data.datapointColumns.forEach(dpId => {
    const colValues = data.values.get(dpId) || [];
    const validValues = colValues.filter(v => v !== null && !isNaN(v));

    if (validValues.length === 0) return;

    const min = Math.min(...validValues);
    const max = Math.max(...validValues);
    const meta = data.metadata.columnMap.get(dpId);

    columnRanges.push({
      datapointId: dpId,
      min,
      max,
      range: max - min,
      meta
    });
  });

  // Sort by range and cluster
  const groups = {
    status: { name: 'Status', side: 'left', columns: [], range: [0, 15], domain: [0, 0.12] },
    temperature: { name: 'Teplota (°C)', side: 'left', columns: [], range: [-30, 100], domain: [0.15, 1] },
    percent: { name: '%', side: 'left', columns: [], range: [0, 100], domain: [0.15, 1] },
    rpm: { name: 'RPM', side: 'right', columns: [], range: [0, 6000], domain: [0.15, 1] },
    pressure: { name: 'Tlak (bar)', side: 'right', columns: [], range: [0, 50], domain: [0.15, 1] },
    power: { name: 'Výkon (kW)', side: 'right', columns: [], range: [0, 20], domain: [0.15, 1] }
  };

  columnRanges.forEach(col => {
    const unit = col.meta?.unit?.toLowerCase() || '';
    const axisGroup = col.meta?.axisGroup || '';
    const label = col.meta?.label?.toLowerCase() || '';

    // Classify by unit, axisGroup, or value range
    if (label.includes('mode') || label.includes('status') || label.includes('stav') ||
        (col.max <= 15 && col.min >= 0 && Number.isInteger(col.min) && Number.isInteger(col.max))) {
      groups.status.columns.push(col);
    } else if (unit.includes('°c') || unit === 'k' || unit === ' k' || axisGroup === 'temperature') {
      // Temperature: °C or Kelvin (but not kW/kWh)
      groups.temperature.columns.push(col);
    } else if (unit.includes('%') || axisGroup === 'percent') {
      groups.percent.columns.push(col);
    } else if (unit.includes('rpm') || axisGroup === 'speed') {
      groups.rpm.columns.push(col);
    } else if (unit.includes('bar') || axisGroup === 'pressure') {
      groups.pressure.columns.push(col);
    } else if (unit === 'w' || unit === ' w' || axisGroup === 'power') {
      // Power in W only (will be converted to kW)
      groups.power.columns.push(col);
    } else if (unit === 'kw' || unit === ' kw') {
      // Power already in kW - no conversion needed, mark specially
      col.alreadyKW = true;
      groups.power.columns.push(col);
    } else if (unit.includes('kwh')) {
      // Energy (kWh) - skip for now, too large values
    } else if (col.max > 1000) {
      // Large values → probably RPM or power
      groups.rpm.columns.push(col);
    } else if (col.min < 0 || col.max > 50) {
      // Could be temperature
      groups.temperature.columns.push(col);
    } else {
      // Default to percent/dimensionless
      groups.percent.columns.push(col);
    }
  });

  // Adjust ranges based on actual data
  Object.entries(groups).forEach(([groupName, group]) => {
    if (group.columns.length > 0) {
      let allMin = Math.min(...group.columns.map(c => c.min));
      let allMax = Math.max(...group.columns.map(c => c.max));

      // Convert W to kW for power group (check if any column is NOT already in kW)
      if (groupName === 'power') {
        const hasWatts = group.columns.some(c => !c.alreadyKW);
        if (hasWatts) {
          // Mixed or W only - convert range
          allMin = allMin / 1000;
          allMax = allMax / 1000;
        }
        // If all are already kW, keep as is
      }

      const padding = (allMax - allMin) * 0.1 || 1;
      group.range = [Math.floor(allMin - padding), Math.ceil(allMax + padding)];
    }
  });

  return groups;
}

// Render Plotly chart
async function renderChart(data) {

  if (!plotlyDiv.value) {
    console.error('❌ plotlyDiv.value is null!');
    return;
  }

  const axisGroups = calculateAxisGroups(data);

  const traces = [];

  // Define axis assignments
  const axisMap = {
    status: 'y5',
    temperature: 'y',
    percent: 'y2',
    rpm: 'y3',
    pressure: 'y4',
    power: 'y3' // Share with RPM
  };

  // Shorten label helper
  const shortenLabel = (label) => {
    if (!label) return label;
    return label
      .replace(/Heat Pump/gi, 'HP')
      .replace(/Temperature/gi, 'Temp')
      .replace(/Compressor/gi, 'Comp');
  };

  // Color palette for unique trace colors
  const traceColors = [
    '#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6',
    '#1abc9c', '#e67e22', '#34495e', '#16a085', '#c0392b',
    '#2980b9', '#27ae60', '#f1c40f', '#8e44ad', '#d35400',
    '#00bcd4', '#ff5722', '#795548', '#607d8b', '#4caf50',
    '#ff9800', '#9c27b0', '#03a9f4', '#8bc34a', '#ffc107'
  ];
  let colorIdx = 0;

  // Track used names to avoid duplicates
  const usedNames = new Set();

  // Create traces for each group
  Object.entries(axisGroups).forEach(([groupName, group]) => {
    group.columns.forEach(col => {
      let values = data.values.get(col.datapointId) || [];
      let traceName = shortenLabel(col.meta?.label || col.datapointId);
      let unit = col.meta?.unit || '';

      // Skip duplicates
      if (usedNames.has(traceName)) {
        console.warn('Skipping duplicate trace:', traceName);
        return;
      }
      usedNames.add(traceName);

      // Convert W to kW for power group (unless already in kW)
      if (groupName === 'power' && !col.alreadyKW) {
        values = values.map(v => v !== null ? v / 1000 : null);
        unit = 'kW';
      } else if (groupName === 'power' && col.alreadyKW) {
        unit = 'kW';
      }

      // Get unique color for this trace
      const traceColor = traceColors[colorIdx % traceColors.length];
      colorIdx++;

      traces.push({
        x: data.timestamps,
        y: values,
        name: traceName,
        type: 'scatter',
        mode: 'lines',
        line: {
          color: traceColor,
          width: groupName === 'status' ? 1 : 1.5
        },
        yaxis: axisMap[groupName],
        connectgaps: false,
        hovertemplate: `<b>${traceName}</b><br>` +
                      `%{x|%d.%m.%Y %H:%M:%S}<br>` +
                      `%{y:.2f} ${unit}<extra></extra>`
      });
    });
  });

  const layout = {
    title: false,
    showlegend: true,
    legend: {
      orientation: 'h',
      x: 0.5,
      y: -0.15,
      xanchor: 'center',
      yanchor: 'top',
      font: { size: 10 }
    },
    hovermode: 'closest',

    // Main X axis
    xaxis: {
      type: 'date',
      domain: [0.08, 0.92],
      rangeslider: {
        visible: true,
        thickness: 0.08
      }
    },

    // Y1: Temperature (left main)
    yaxis: {
      title: { text: 'Teplota (°C)', font: { size: 11 } },
      side: 'left',
      domain: [0.18, 1],
      range: axisGroups.temperature.range,
      showgrid: true,
      gridcolor: '#e5e7eb'
    },

    // Y2: Percent (left, overlay)
    yaxis2: {
      title: { text: '%', font: { size: 11 } },
      side: 'left',
      overlaying: 'y',
      position: 0,
      range: axisGroups.percent.range,
      showgrid: false
    },

    // Y3: RPM (right)
    yaxis3: {
      title: { text: 'RPM', font: { size: 11 } },
      side: 'right',
      overlaying: 'y',
      range: axisGroups.rpm.range,
      showgrid: false
    },

    // Y4: Pressure (right, second position)
    yaxis4: {
      title: { text: 'bar', font: { size: 11 } },
      side: 'right',
      overlaying: 'y',
      position: 1,
      range: axisGroups.pressure.range,
      showgrid: false
    },

    // Y5: Status (bottom)
    yaxis5: {
      title: { text: 'Status', font: { size: 10 } },
      side: 'left',
      domain: [0, 0.12],
      range: axisGroups.status.range,
      showgrid: true,
      gridcolor: '#e5e7eb'
    },

    margin: { t: 30, r: 60, b: 100, l: 60 },
    autosize: true,
    height: null, // Auto height

    // Subplot grid
    grid: {
      rows: 2,
      columns: 1,
      pattern: 'independent',
      roworder: 'top to bottom'
    }
  };

  const config = {
    displayModeBar: true,
    displaylogo: false,
    modeBarButtonsToRemove: ['lasso2d', 'select2d'],
    responsive: true,
    toImageButtonOptions: {
      format: 'png',
      filename: `sec-analysis-${new Date().toISOString().split('T')[0]}`,
      height: 1200,
      width: 1920,
      scale: 2
    }
  };


  try {
    await Plotly.newPlot(plotlyDiv.value, traces, layout, config);
  } catch (err) {
    console.error('❌ Plotly.newPlot error:', err);
  }

  // Add hover event for ruler
  plotlyDiv.value.on('plotly_hover', (eventData) => {
    if (!showRuler.value || !eventData.points || eventData.points.length === 0) return;

    const point = eventData.points[0];
    const pointIndex = point.pointIndex;
    const xInDataCoord = point.x;

    // Calculate pixel position
    const xaxis = plotlyDiv.value._fullLayout.xaxis;
    if (xaxis) {
      const xPixel = xaxis.l2p(xaxis.d2l(xInDataCoord)) + xaxis._offset;
      rulerX.value = xPixel;
    }

    // Collect ALL values from ALL traces at this index
    const timestamp = new Date(xInDataCoord);
    const values = [];
    const allTraces = plotlyDiv.value.data;

    allTraces.forEach((trace, idx) => {
      if (trace.y && trace.y[pointIndex] !== undefined && trace.y[pointIndex] !== null) {
        values.push({
          id: trace.name,
          label: trace.name,
          value: trace.y[pointIndex],
          color: trace.line?.color || '#888',
          unit: extractUnit(trace.hovertemplate) || '',
          decimals: 2
        });
      }
    });

    rulerData.value = {
      timestamp,
      values
    };
  });
}

function extractUnit(hovertemplate) {
  if (!hovertemplate) return '';
  // Extract unit from template like "... %{y:.2f} °C<extra>"
  const match = hovertemplate.match(/%\{y[^}]*\}\s*([^<]*)/);
  return match ? match[1].trim() : '';
}

// Start analysis
async function startAnalysis() {
  if (!selectedFile.value) {
    error.value = 'Vyberte CSV soubor';
    return;
  }

  isProcessing.value = true;
  error.value = null;
  processingProgress.value = 10;
  processingStatus.value = 'Čtu soubor...';

  try {
    // Read file
    const content = await readFile(selectedFile.value);
    processingProgress.value = 30;
    processingStatus.value = 'Parsuju CSV data...';

    // Parse CSV
    const data = await parseSecCloudCSV(content);
    processingProgress.value = 80;
    processingStatus.value = 'Vytvářím graf...';

    chartData.value = data;

    // Set processing to false BEFORE rendering so plotlyDiv is in DOM
    processingProgress.value = 100;
    processingStatus.value = 'Hotovo!';
    isProcessing.value = false;

    // Wait for DOM update (now plotlyDiv should exist)
    await nextTick();

    // Render chart
    await renderChart(data);

    emit('analysis-complete', data);

  } catch (err) {
    console.error('Analysis error:', err);
    error.value = err.message || 'Nepodařilo se analyzovat soubor';
    isProcessing.value = false;
    processingProgress.value = 0;
  }
}

// Read file as text
function readFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(new Error('Nepodařilo se přečíst soubor'));
    reader.readAsText(file, 'UTF-8');
  });
}

// Download CSV export
function downloadCSV() {
  // TODO: Implement CSV export
  alert('Export CSV - bude implementováno');
}

// Watch for modal open/close
watch(() => props.isOpen, (isOpen) => {
  if (!isOpen) {
    // Clean up when modal closes
    if (plotlyDiv.value) {
      Plotly.purge(plotlyDiv.value);
    }
  }
});

// Handle resize
onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  if (plotlyDiv.value) {
    Plotly.purge(plotlyDiv.value);
  }
});

function handleResize() {
  if (plotlyDiv.value && chartData.value) {
    Plotly.Plots.resize(plotlyDiv.value);
  }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 0.3s ease;
}

.modal-enter-from > div,
.modal-leave-to > div {
  transform: scale(0.95);
}

/* Custom scrollbar for ruler panel */
.floating-ruler .max-h-\[70vh\]::-webkit-scrollbar {
  width: 4px;
}

.floating-ruler .max-h-\[70vh\]::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.floating-ruler .max-h-\[70vh\]::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.floating-ruler .max-h-\[70vh\]::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}
</style>
